import { useFormContext } from 'react-hook-form';
import { FieldDefinition } from './dynamic-form.types.ts';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Textarea } from '@/components/ui/textarea.tsx';
import { Checkbox } from '@/components/ui/checkbox.tsx';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx';
import { MultiSelect } from '@/components/ui/multi-select.tsx';
import { Slider } from '@/components/ui/slider.tsx';
import { normalizeOptions, getValidationRules } from './dynamic-form-utils.ts';
import { Label } from '@/components/ui/label.tsx';
import { cn } from '@/utils/utils.ts';

interface DynamicFormFieldProps {
  name: string;
  field: FieldDefinition;
}

export const DynamicFormField = ({ name, field }: DynamicFormFieldProps) => {
  const { control } = useFormContext();
  const validationRules = getValidationRules(field);

  const renderInput = (fieldProps: { value: unknown; onChange: (value: unknown) => void }) => {
    const { value, onChange } = fieldProps;

    switch (field.type) {
      case 'number':
        return (
          <div className="relative">
            <Input
              type="number"
              min={field.min}
              max={field.max}
              step={field.step}
              placeholder={field.placeholder}
              disabled={field.disabled}
              value={value as number}
              onChange={(e) => onChange(e.target.valueAsNumber || 0)}
              className={cn(field.unit && 'pr-10')}
            />
            {field.unit && (
              <span className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm">
                {field.unit}
              </span>
            )}
          </div>
        );

      case 'text':
      case 'email':
      case 'password':
        return (
          <div className="relative">
            <Input
              type={field.type}
              placeholder={field.placeholder}
              disabled={field.disabled}
              maxLength={field.maxLength}
              value={(value as string) ?? ''}
              onChange={(e) => onChange(e.target.value)}
              className={cn(field.unit && 'pr-12')}
            />
            {field.unit && (
              <span className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm">
                {field.unit}
              </span>
            )}
          </div>
        );

      case 'textarea':
        return (
          <Textarea
            placeholder={field.placeholder}
            disabled={field.disabled}
            maxLength={field.maxLength}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            className="h-full"
          />
        );

      case 'checkbox':
        return (
          <div className="flex items-center space-x-2">
            <Checkbox
              disabled={field.disabled}
              checked={value as boolean}
              onCheckedChange={onChange}
            />
            <Label className="text-sm leading-none font-normal peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              {field.label}
            </Label>
          </div>
        );

      case 'select':
        return (
          <div className="relative">
            <Select
              disabled={field.disabled}
              value={(value as string) ?? ''}
              onValueChange={onChange}
            >
              <SelectTrigger className={cn(field.unit && 'pr-12')}>
                <SelectValue placeholder={field.placeholder || 'Select an option'} />
              </SelectTrigger>
              <SelectContent>
                {normalizeOptions(field.options).map((option) => (
                  <SelectItem
                    key={String(option.value)}
                    value={String(option.value)}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {field.unit && (
              <span className="text-muted-foreground pointer-events-none absolute top-1/2 right-10 -translate-y-1/2 text-sm">
                {field.unit}
              </span>
            )}
          </div>
        );

      case 'multiselect':
        return (
          <MultiSelect
            options={normalizeOptions(field.options).map((opt) => ({
              label: opt.label,
              value: String(opt.value),
            }))}
            defaultValue={value as string[]}
            onValueChange={onChange}
            placeholder={field.placeholder || 'Select options'}
            disabled={field.disabled}
          />
        );

      case 'radio':
        return (
          <RadioGroup disabled={field.disabled} value={String(value)} onValueChange={onChange}>
            {normalizeOptions(field.options).map((option) => (
              <div key={String(option.value)} className="flex items-center space-x-2">
                <RadioGroupItem
                  value={String(option.value)}
                  disabled={option.disabled}
                  id={`${name}-${option.value}`}
                />
                <Label htmlFor={`${name}-${option.value}`} className="font-normal">
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );

      case 'range':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-sm">{field.min ?? 0}</span>
              <span className="text-sm font-medium">
                {value as number}
                {field.unit && ` ${field.unit}`}
              </span>
              <span className="text-muted-foreground text-sm">{field.max ?? 100}</span>
            </div>
            <Slider
              min={field.min ?? 0}
              max={field.max ?? 100}
              step={field.step ?? 1}
              disabled={field.disabled}
              value={[value as number]}
              onValueChange={(vals) => onChange(vals[0])}
            />
          </div>
        );

      case 'date':
        return (
          <Input
            type="date"
            placeholder={field.placeholder}
            disabled={field.disabled}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );

      case 'time':
        return (
          <Input
            type="time"
            placeholder={field.placeholder}
            disabled={field.disabled}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );

      case 'datetime':
        return (
          <Input
            type="datetime-local"
            placeholder={field.placeholder}
            disabled={field.disabled}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );

      case 'color':
        return (
          <div className="flex items-center gap-2">
            <Input
              type="color"
              disabled={field.disabled}
              value={(value as string) ?? ''}
              onChange={(e) => onChange(e.target.value)}
              className="h-10 w-20"
            />
            <Input
              type="text"
              disabled={field.disabled}
              value={(value as string) ?? ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder="#000000"
            />
          </div>
        );

      case 'file':
        return (
          <Input
            type="file"
            disabled={field.disabled}
            onChange={(e) => {
              const files = e.target.files;
              onChange(files ? files[0] : null);
            }}
          />
        );

      default:
        return (
          <Input
            placeholder={field.placeholder}
            disabled={field.disabled}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );
    }
  };

  return (
    <div className={cn('w-full max-w-[200px]', field.class)}>
      <FormField
        control={control}
        name={name}
        rules={validationRules}
        render={({ field: formField }) => (
          <FormItem
            className={cn(
              'h-full',
              field.type === 'checkbox' && 'flex flex-row items-start space-y-0 space-x-3'
            )}
          >
            {field.type !== 'checkbox' && <FormLabel>{field.label}</FormLabel>}
            <FormControl>{renderInput(formField)}</FormControl>
            {field.description && <FormDescription>{field.description}</FormDescription>}
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
